import useEmblaCarousel from 'embla-carousel-react'
import useSWR, { SWRResponse } from 'swr'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { useState, useEffect } from 'react'
import {
  AppBar,
  Dialog,
  Toolbar,
  Typography,
  Container,
  IconButton,
  Button,
  Stack,
} from '@mui/material'
import { useLocation } from 'react-router-dom'
import Loading from '../../components/Loading'
import Logo from '../../components/Logo'
import Thumb from './components/Thumb'
import Slide from './components/Slide'
import axiosInstance from '../../utils/axiosInstance'
import { OrderPreview } from '../../types/task'
import { downloadFilesAsZip } from '../../utils/tools'
import './style.scss'

const Preview = () => {
  const location = useLocation()
  const id = new URLSearchParams(location?.search).get('id')
  const { data, isLoading }: SWRResponse = useSWR(`/order-previews?id=${id}`, axiosInstance)
  const [slideIndex, setSlideIndex] = useState<number>(0)

  const [emblaRef, emblaApi] = useEmblaCarousel()
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({ axis: 'y' })

  const images: OrderPreview[] = data?.data?.previews || []
  const audios: OrderPreview[] = data?.data?.previewsAudio || []
  const videos: OrderPreview[] = data?.data?.previewsVideo || []
  const slides: OrderPreview[] = [...images, ...audios, ...videos]

  const filesForZip = slides.map((item, index) => ({
    id: index,
    name: item.name,
    url: item.image.url,
  }))

  const next = () => {
    let newIndex = slideIndex + 1
    if (newIndex > slides.length - 1) {
      newIndex = 0
    }
    emblaApi?.scrollNext()
    emblaThumbsApi?.scrollTo(newIndex)
    setSlideIndex(newIndex)
  }
  const prev = () => {
    let newIndex = slideIndex - 1
    if (newIndex < 0) {
      newIndex = slides.length - 1
    }
    emblaApi?.scrollPrev()
    emblaThumbsApi?.scrollTo(newIndex)
    setSlideIndex(newIndex)
  }

  const goToSlide = (index) => {
    emblaApi?.scrollTo(index)
    emblaThumbsApi?.scrollTo(index)
    setSlideIndex(index)
  }

  useEffect(() => {
    if (emblaApi)
      emblaApi.on('select', (api) => {
        const index = api.selectedScrollSnap()
        emblaThumbsApi?.scrollTo(index)
        setSlideIndex(index)
      })
  }, [emblaApi, emblaThumbsApi])

  useEffect(() => {
    const errorHandler = (e) => {
      if (
        e.message === 'ResizeObserver loop limit exceeded' ||
        e.message.includes('ResizeObserver loop completed')
      ) {
        e.stopImmediatePropagation()
        const resizeObserverErrDiv = document.getElementById(
          'webpack-dev-server-client-overlay-div'
        )
        const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay')
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none')
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none')
        }
      }
    }
    window.addEventListener('error', errorHandler)
    return () => window.removeEventListener('error', errorHandler)
  }, [])

  return (
    <Dialog fullScreen open>
      <AppBar sx={{ position: 'sticky' }} color="default">
        <Toolbar>
          <Stack width="100%" direction="row" alignItems="center" justifyContent="center">
            <Typography variant="h3" pl={25}>
              <Logo />
            </Typography>
            <Stack width="40%" alignItems="flex-end">
              <Button variant="contained" onClick={() => downloadFilesAsZip(filesForZip)}>
                Download all
              </Button>
            </Stack>
          </Stack>
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" className="OrderPreview">
        {isLoading && <Loading />}
        {!isLoading && (
          <>
            <div className="embla-thumb-wrap">
              <div className="embla-thumb" ref={emblaThumbsRef}>
                <div className="embla-thumb-container">
                  {slides.map((thumb, index) => {
                    return (
                      <div
                        onClick={() => goToSlide(index)}
                        className={`embla-thumb-slide ${
                          slideIndex === index ? 'embla-thumb-slide-active' : ''
                        }`}
                        key={`thumb-${index}`}
                      >
                        <Thumb thumb={thumb} />
                      </div>
                    )
                  })}
                </div>
              </div>
              <div className="embla-thumb-btns">
                <IconButton onClick={prev} disabled={!slideIndex}>
                  <KeyboardArrowLeftIcon />
                </IconButton>
                <IconButton onClick={next} disabled={slideIndex === slides.length - 1}>
                  <KeyboardArrowRightIcon />
                </IconButton>
              </div>
            </div>

            <div className="embla" ref={emblaRef}>
              <div className="embla-container">
                {slides.map((slide, index) => {
                  return (
                    <div className="embla-slide" key={`slide-${index}`}>
                      <Slide slide={slide} />
                    </div>
                  )
                })}
              </div>
            </div>
          </>
        )}
      </Container>
    </Dialog>
  )
}

export default Preview
